import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import Logo from "../components/Logo";
import Icon from "@mdi/react";
import { mdiMapMarker } from "@mdi/js";
import BackButton from "../components/BackButton";
import { ReservationService } from "../services/ReservationService";
import { userService } from "../services/UserService";
import { formatReservationDateTime } from "../helpers/formatDateLabel";

export default function MyReservations() {
  const service = new ReservationService();
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReservations = async () => {
      try {
        const data = await service.getReservations();
        setReservations(data?.items || []);
        setError(null);
      } catch (err) {
        console.error("Erro ao buscar reservas:", err);
        setError("Erro ao carregar reservas. Tente novamente.");
      } finally {
        setLoading(false);
      }
    };

    fetchReservations();
  }, []);

  const handleShowMap = ({ street, number, neighborhood, city, zipCode }) => {
    const query = `${street} ${number} - ${neighborhood}, ${city}, ${zipCode}`;
    window.open(
      `https://www.google.com/maps/place/${encodeURIComponent(query)}`
    );
  };

  const formatDateLabel = (reservationDate, type) => {
    const reservationDateObj = DateTime.fromISO(reservationDate, {
      zone: "utc",
    })
      .setZone("America/Sao_Paulo")
      .startOf("day");

    const today = DateTime.now().setZone("America/Sao_Paulo").startOf("day");
    const yesterday = today.minus({ days: 1 });
    const tomorrow = today.plus({ days: 1 });

    const weekdays = [
      "DOMINGO",
      "SEGUNDA",
      "TERÇA",
      "QUARTA",
      "QUINTA",
      "SEXTA",
      "SÁBADO",
    ];

    let dateLabel;
    if (reservationDateObj.equals(yesterday)) {
      dateLabel = "ONTEM";
    } else if (reservationDateObj.equals(tomorrow)) {
      dateLabel = "AMANHÃ";
    } else {
      dateLabel = weekdays[reservationDateObj.weekday % 7];
    }

    return `${dateLabel} - ${type === "entry" ? "ENTRADA" : "SAÍDA"}`;
  };

  const canCancel = (reservationDate) => {
    const now = new Date();
    const reservationTime = new Date(reservationDate);
    return reservationTime - now > 24 * 60 * 60 * 1000;
  };

  const handleCancelReservation = async (reservation) => {
    try {
      navigate(`/confirm-cancel-reservation?reservation=${reservation._id}`);
    } catch (error) {
      console.error("Erro ao cancelar a reserva:", error);
      alert("Erro ao cancelar a reserva.");
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <h2>Carregando reservas...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-5">
        <BackButton />
        <h2>{error}</h2>
      </div>
    );
  }

  if (reservations.length === 0) {
    return (
      <div className="text-center p-5">
        <BackButton />
        <h2>Nenhuma reserva encontrada.</h2>
      </div>
    );
  }

  return (
    <div className="container p-5">
      <div
        className="d-flex align-items-center justify-content-center position-relative"
        style={{ height: "125px" }}
      >
        <BackButton />
        <Logo center className="mb-4" size="125px" />
      </div>

      <div>
        <h1 className="text-center text-uppercase fw-bold mb-5">
          Minhas Reservas
        </h1>
      </div>

      {reservations.map((reservation, index) => (
        <div className="row" key={reservation._id || index}>
          <div className="col-12 mb-4 text-center">
            <div className="mb-2">
              <div className="fw-bold text-uppercase fs-2 mb-2">
                {formatDateLabel(reservation.date, reservation.type)}
              </div>
              <div>{formatReservationDateTime(reservation.startHour)}</div>
            </div>
            <div>
              <div>{reservation.school?.name}</div>
              <div>{reservation.school?.address?.neighborhood}</div>
            </div>
          </div>

          <div className="col-12">
            <div className="px-3">
              <div
                className="row align-items-center justify-content-center border border-secondary border-2 rounded-4 pb-3 pt-3"
                onClick={() => handleShowMap(reservation.school?.address)}
                style={{ cursor: "pointer" }}
              >
                <div
                  className="col-1"
                  style={{ width: "fit-content", marginLeft: "-25px" }}
                >
                  <Icon path={mdiMapMarker} size={1} color="secondary" />
                </div>
                <div
                  className="col-11 text-center"
                  style={{ width: "fit-content" }}
                >
                  {reservation.school?.address?.street},{" "}
                  {reservation.school?.address?.number}
                  <br />
                  {reservation.school?.address?.neighborhood}
                  <br />
                  {reservation.school?.address?.city} -{" "}
                  {reservation.school?.address?.zipCode}
                </div>
              </div>
            </div>

            {canCancel(reservation.date) && (
              <div className="text-center mt-3">
                <button
                  className="btn btn-danger"
                  onClick={() => handleCancelReservation(reservation)}
                >
                  CANCELAR RESERVA
                </button>
              </div>
            )}

            <hr className="my-4 border-2 border-secondary" />
          </div>
        </div>
      ))}
    </div>
  );
}
